import { useMemo } from 'react'
import useAuth from '../models/useAuth'
import useProfile from '../models/useProfile'
import useNetwork from '../models/useNetwork'
import useSession from '../models/useSession'
import useNode from '../models/useNode'
import useNodeMetadata from '../models/useNodeMetadata'
import useSetPlaces from '../models/useSetPlaces'
import { withSetPlaces } from '../utilities/setPlaces'
import useServices from '../models/useServices'
import useOrgMembers from '../models/useOrgMembers'
import { userText } from '../utilities/userLabel'

const useMergedData = (): MergedData => {
  const networks = useNetwork(state => state.networks)
  const nodes = useNode(state => state.nodes)
  const derivedMetadata = useNodeMetadata(state => state.nodeMetadata)
  // ⚠ A place a person set goes over the place of the address (nobgp-router#1976): the graph groups
  // the node under its set place, and the map draws it there.
  const setPlaces = useSetPlaces(state => state.places)
  const nodeMetadata = useMemo(() => withSetPlaces(derivedMetadata, setPlaces), [derivedMetadata, setPlaces])
  const services = useServices(state => state.services)
  const sessions = useSession(state => state.sessions)
  const user = useAuth(state => state.user)
  const fullName = useProfile(state => state.fullName)
  const avatarImage = useProfile(state => state.avatarImage)
  const members = useOrgMembers(state => state.members)

  // One account (center) node per distinct network owner, so an org's shared
  // networks render — not just your own (the graph is owner-centric). You get your
  // profile name/avatar; co-owners are resolved from the active org's roster (their
  // profiles aren't readable client-side via RLS), falling back to the id.
  const memberById = new Map(members.map(m => [m.userId, m]))
  // ⚠ The reader first: the "No network" hub hangs off the first account.
  const ownerIds = new Set<string>(user?.id ? [user.id] : [])
  for (const n of networks) if (n.ownerId) ownerIds.add(String(n.ownerId))
  const accounts: AccountRecord[] = [...ownerIds].map(id => {
    // `userText` is the app's one person label (app#401): the name, else the email. A
    // graph label has no hover, so the email shows only when there is no name.
    if (id === user?.id) return { id, name: userText({ name: fullName, email: user?.email }, 'You'), url: avatarImage ?? null }
    return { id, name: userText(memberById.get(id) ?? {}, 'Member'), url: null }
  })

  const data = {
    nodes,
    nodeMetadata,
    services,
    networks,
    sessions,
    accounts,
  }

  return validateData(data)
}

function validateData(data: MergedData) {
  const validAccounts = new Set(data.accounts.map(a => a.id))
  const validNetworks = new Set(data.networks.filter(n => n.ownerId && validAccounts.has(n.ownerId)).map(n => n.id))
  const nodes = data.nodes.filter(n => n.networkIds.some(id => validNetworks.has(id)))
  // ⚠ Not in `nodes`: every list groups `nodes` by network. A node in another org's network only is
  // in neither (`useOutsideNodes`).
  const noNetworkNodes = data.nodes.filter(n => !n.networkIds.length)
  const validNodes = new Set([...nodes, ...noNetworkNodes].map(n => n.id))

  return {
    accounts: data.accounts.filter(a => validAccounts.has(a.id)),
    networks: data.networks.filter(n => validNetworks.has(n.id)),
    nodes,
    noNetworkNodes,
    nodeMetadata: data.nodeMetadata.filter(m => validNodes.has(m.nodeId)),
    services: data.services.filter(s => validNodes.has(s.nodeId)),
    sessions: data.sessions,
  }
}

export default useMergedData
